// Canonical source of the Contribution Agreement wording.
//
// CRITICAL INVARIANT: this file is the SINGLE source of truth for the terms.
// ContributionAgreementScreen RENDERS from AGREEMENT_SECTIONS / AGREEMENT_DISCLAIMERS,
// and the consent record's terms_sha256 is the SHA-256 of AGREEMENT_PLAIN_TEXT —
// which is derived from those same constants. Because the displayed text and the
// hashed text both come from here, the stored hash CANNOT drift from what the
// member actually saw. Never hardcode the terms text anywhere else.
//
// Bump AGREEMENT_VERSION whenever ANY wording below changes — the version + hash
// together prove exactly which terms a member accepted.

// v3 is a TEXT change (hash-backed bump): adds three clauses — Continuing
// Contributions (pay-to-the-end), Late Fees, and Legal Name. Because the wording
// changed, terms_sha256 moves with the version. (v1 = original terms; v2 = a
// policy-only bump for the full-name era, wording unchanged.) Forward-only:
// existing consent rows keep their prior version + hash.
export const AGREEMENT_VERSION = 'v3-2026-07';

export const AGREEMENT_TITLE = 'Contribution Agreement';

export const AGREEMENT_SECTIONS = [
  {
    heading: 'Member Responsibilities',
    body: 'As a member of this savings group, I agree to contribute the agreed amount on the scheduled due dates. Timely contributions are essential for the group\'s success and for maintaining trust among all members.',
  },
  {
    heading: 'Continuing Contributions',
    body: 'I understand that collecting my payout does not reduce, pause, or end my duty to keep contributing. After I collect, I still owe every remaining contribution until the cycle ends. Every member relies on this so that each person receives a full payout in turn.',
  },
  {
    heading: 'Default Consequences',
    body: 'I understand that failure to contribute on time may result in default status, which could affect my position in the rotation and my relationship with other group members. The group may establish its own consequences for late or missed contributions.',
  },
  {
    heading: 'Late Fees',
    body: 'I understand the group may charge a late fee when I miss a contribution or pay after the due date. The group sets the late fee amount and terms. A late fee is separate from my contribution and is added to what I owe.',
  },
  {
    heading: 'EsuSol Role',
    body: 'I acknowledge that EsuSol is a platform that facilitates group management and communication. EsuSol does not hold, process, or guarantee any funds. All financial transactions occur directly between group members.',
  },
  {
    heading: 'Financial Risk',
    body: 'I understand that all financial risk remains with the group members. EsuSol is not responsible for defaulted contributions, disputes between members, or any financial losses. I participate at my own risk.',
  },
  {
    heading: 'Member Commitment',
    body: 'I commit to being an active, responsible member of this group, communicating openly with other members, and honoring my financial commitments to the best of my ability.',
  },
  {
    heading: 'Legal Name',
    body: 'I confirm that the name I have signed above is my full legal name.',
  },
];

export const AGREEMENT_DISCLAIMERS_TITLE = 'Important Disclaimers';

export const AGREEMENT_DISCLAIMERS = [
  'EsuSol does NOT hold or process money',
  'EsuSol does NOT guarantee payouts',
  'All financial risk remains with members',
  'This is a legally binding agreement',
];

// Deterministic plain-text serialization — the EXACT text that gets hashed.
// Do not reorder/reformat casually: any change alters the hash and must be
// accompanied by an AGREEMENT_VERSION bump.
export const AGREEMENT_PLAIN_TEXT = [
  AGREEMENT_TITLE,
  ...AGREEMENT_SECTIONS.map((s) => `${s.heading}: ${s.body}`),
  AGREEMENT_DISCLAIMERS_TITLE,
  ...AGREEMENT_DISCLAIMERS.map((d) => `- ${d}`),
].join('\n\n');
